import {Navigate, useLocation} from "react-router-dom";
import UserService from "../../services/user-service";
import React, {useEffect, useState} from "react";
import { Settings } from "../Settings";

interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }): JSX.Element => {
    const location = useLocation();
    const [status, setStatus] = useState<'checking' | 'authed' | 'unauth'>('checking');

    useEffect(() => {
        let mounted = true;
        const checkAuth = async () => {
            try {
                // In offline mode, treat as authenticated and skip any server calls
                if (Settings.useOfflineMode) {
                    if (mounted) setStatus('authed');
                    return;
                }
                // Fast path: if already authenticated in memory
                if (UserService.isAuthenticated()) {
                    if (mounted) setStatus('authed');
                    return;
                }
                // Attempt to initialize from cookies via refresh flow
                const user = await UserService.initUserState();
                if (!mounted) return;
                setStatus(user ? 'authed' : 'unauth');
            } catch (e) {
                if (mounted) setStatus('unauth');
            }
        };
        checkAuth();
        return () => { mounted = false; };
    }, []);

    // Handle logout events after mount (tokens cleared) without flicker
    if (status === 'authed' && !Settings.useOfflineMode && !UserService.isAuthenticated()) {
        return <Navigate to="/login" replace state={{ from: location }} />
    }

    if (status === 'checking') {
        return <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh'}}>
            <i className="fas fa-spinner start-spinner" />
        </div>
    }

    if (status === 'unauth') {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
